import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { API, graphqlOperation } from 'aws-amplify';
import { listUsers } from '../graphql/queries';
import { Loading } from 'element-react';
import { convertCentsToDollar, formatOrderDate } from '../utils';

const CRMWrap = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const CRMHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(31, 45, 61, 0.05);
  border: 1px solid rgba(31, 45, 61, 0.08);

  /* subtle scrollbar indicator for mobile users */
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(31, 45, 61, 0.15);
    border-radius: 999px;
  }
`;

const CRMTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  min-width: 700px;

  th, td {
    padding: 16px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.05);
  }

  th {
    background: rgba(31, 45, 61, 0.03);
    color: #6a7b8a;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: rgba(1, 191, 113, 0.02);
  }

  td {
    color: #1f2d3d;
    font-size: 0.95rem;
  }
`;

const EmailDiv = styled.div`
  color: #6a7b8a;
  font-size: 0.85rem;
`;

const CRM = () => {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchAllUsers = async () => {
      setIsLoading(true);
      try {
        let allUsers = [];
        let nextToken = null;

        do {
          const result = await API.graphql(graphqlOperation(listUsers, { 
            limit: 200,
            nextToken 
          }));
          
          if (result.data && result.data.listUsers) {
            allUsers = allUsers.concat(result.data.listUsers.items);
            nextToken = result.data.listUsers.nextToken;
          } else {
            nextToken = null;
          }
        } while (nextToken);

        if (isMounted) {
          processCustomerData(allUsers);
        }
      } catch (err) {
        console.error("Error fetching CRM users", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchAllUsers();

    return () => { isMounted = false; };
  }, []);

  const processCustomerData = (users) => {
    const customerArray = [];

    users.forEach(user => {
      if (!user || !user.id) return;
      
      let lifetimeValue = 0;
      let totalOrders = 0;
      let latestOrderDate = null;
      
      // Calculate from nested orders returned by AppSync
      if (user.orders && user.orders.items) {
        user.orders.items.forEach(order => {
           if (!order) return;
           totalOrders += 1;
           const orderPrice = order.price || order.init_price || 0;
           lifetimeValue += orderPrice;

           if (!latestOrderDate || new Date(order.createdAt) > new Date(latestOrderDate)) {
             latestOrderDate = order.createdAt;
           }
        });
      }
      
      customerArray.push({
        id: user.id,
        username: user.username,
        email: user.email,
        totalOrders,
        lifetimeValue,
        latestOrderDate: latestOrderDate || new Date().toISOString()
      });
    });

    // Sort by LTV descending by default
    customerArray.sort((a, b) => b.lifetimeValue - a.lifetimeValue);

    setCustomers(customerArray);
  };

  if (isLoading) {
    return <Loading fullscreen={true} text="Aggregating Customer Data from AWS..." />;
  }

  return (
    <CRMWrap>
      <CRMHeader>
        <h2 style={{ margin: 0 }}>Customer Directory</h2>
        <div style={{ color: '#6a7b8a', fontSize: '0.9rem' }}>
          {customers.length} Customers Found
        </div>
      </CRMHeader>

      <TableWrap>
        <CRMTable>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Total Orders</th>
              <th>Average Order Value</th>
              <th>Lifetime Value (LTV)</th>
              <th>Last Active</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                  No customer data found.
                </td>
              </tr>
            ) : (
              customers.map(c => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.username}</strong>
                    <EmailDiv>{c.email}</EmailDiv>
                  </td>
                  <td>{c.totalOrders}</td>
                  <td>
                    ${convertCentsToDollar(c.totalOrders > 0 ? (c.lifetimeValue / c.totalOrders) : 0)}
                  </td>
                  <td>
                    <strong style={{ color: '#0a8f57' }}>
                      ${convertCentsToDollar(c.lifetimeValue)}
                    </strong>
                  </td>
                  <td>
                    {c.totalOrders > 0 ? formatOrderDate(c.latestOrderDate) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </CRMTable>
      </TableWrap>
    </CRMWrap>
  );
};

export default CRM;
